import { useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { queryClient } from '@/lib/queryClient'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { AlertTriangle, ShieldCheck, KeyRound, User, Mail, Shield } from 'lucide-react'
import { useIncognito } from '@/hooks/use-incognito'
import { Switch } from '@/components/ui/switch'
import { useUIConfig } from '@/hooks/use-ui-config'
import { navigation } from '@/components/layout/Sidebar'

const PERMISSION_DEFINITIONS = [
  {
    action: 'PoolsRead',
    label: 'Pools Read',
    description: 'View pools and pool details',
  },
  {
    action: 'PoolsWrite',
    label: 'Pools Write',
    description: 'Create, edit, and delete pools',
  },
  {
    action: 'PoolsControl',
    label: 'Pools Control',
    description: 'Submit control signals, manage signal timeline, clear signals',
  },
  {
    action: 'PoolsSystemsRead',
    label: 'Pools Systems Read',
    description: 'View systems within pools',
  },
  {
    action: 'PoolsSystemsWrite',
    label: 'Pools Systems Write',
    description: 'Add and remove systems from pools',
  },
  {
    action: 'ChargeSessionsRead',
    label: 'Charge Sessions Read',
    description: 'View charging sessions for systems',
  },
] as const

export default function Settings() {
  const { environment, setEnvironment } = useAuthStore()
  const auth = useAuth()
  const { theme, setTheme } = useThemeStore()
  const { actions, fullAccess } = usePermissionsStore()
  const incognito = useIncognito()
  const { hiddenNavItems, setNavItemVisible } = useUIConfig()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [showProdWarning, setShowProdWarning] = useState(false)
  const [showStagingWarning, setShowStagingWarning] = useState(false)

  const handleEnvironmentChange = async (env: 'staging' | 'production') => {
    // Don't switch if already on that environment
    if (env === environment) return

    // Show warning when switching to production
    if (env === 'production') {
      setShowProdWarning(true)
      return
    }

    // Show warning when switching to staging
    if (env === 'staging') {
      setShowStagingWarning(true)
      return
    }
  }

  const confirmEnvironmentSwitch = async (env: 'staging' | 'production') => {
    // Close dialogs
    setShowProdWarning(false)
    setShowStagingWarning(false)

    // Switch environment
    await setEnvironment(env)

    // Clear all cached data
    queryClient.clear()

    // Log out user (will clear credentials and redirect to login)
    auth.logout()

    toast({
      title: 'Environment Switched',
      description: `Switched to ${env} environment. Please log in again.`,
    })

    navigate('/login')
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your application settings and preferences</p>
      </div>

      {/* Environment */}
      <Card>
        <CardHeader>
          <CardTitle>Environment</CardTitle>
          <CardDescription>Switch between staging and production environments</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant={environment === 'staging' ? 'default' : 'outline'}
              onClick={() => handleEnvironmentChange('staging')}
            >
              Staging
            </Button>
            <Button
              variant={environment === 'production' ? 'default' : 'outline'}
              onClick={() => handleEnvironmentChange('production')}
            >
              Production
            </Button>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              Current: <span className="font-medium capitalize">{environment}</span>
            </p>
            <p className="text-xs font-mono text-muted-foreground">
              {environment === 'production'
                ? 'https://api.gridx.de'
                : 'https://api.staging.gridx.de'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Authentication */}
      <Card>
        <CardHeader>
          <CardTitle>Authentication</CardTitle>
          <CardDescription>Your authentication details and session information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Auth Mode */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
            {auth.authMode === 'AUTH0' ? (
              <ShieldCheck className="h-5 w-5 text-blue-500" />
            ) : (
              <KeyRound className="h-5 w-5 text-amber-500" />
            )}
            <div>
              <p className="text-sm font-medium">
                {auth.authMode === 'AUTH0' ? 'Auth0 Authentication' : 'API Key Authentication'}
              </p>
              <p className="text-xs text-muted-foreground">
                {auth.authMode === 'AUTH0'
                  ? 'Authenticated via Auth0 Single Sign-On'
                  : 'Authenticated via API Key'}
              </p>
            </div>
          </div>

          {/* User Info (Auth0 only) */}
          {auth.authMode === 'AUTH0' && auth.user && (
            <div className="space-y-2 text-sm">
              {auth.user.name && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Name:</span>
                  <span className="font-medium">{incognito.userName(auth.user.name)}</span>
                </div>
              )}
              {auth.user.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Email:</span>
                  <span className="font-medium">{incognito.email(auth.user.email)}</span>
                </div>
              )}
            </div>
          )}

        </CardContent>
      </Card>

      {/* Permissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="mr-2 h-5 w-5" />
            Permissions
          </CardTitle>
          <CardDescription>
            The access rights assigned to your account. Contact your administrator to request
            additional permissions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {fullAccess ? (
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <ShieldCheck className="h-5 w-5 text-green-500 shrink-0" />
              <div>
                <p className="text-sm font-medium">Full Access</p>
                <p className="text-xs text-muted-foreground">
                  Your account has unrestricted access to all features.
                </p>
              </div>
            </div>
          ) : actions === null ? (
            <p className="text-sm text-muted-foreground">Loading permissions…</p>
          ) : (
            <div className="space-y-3">
              {PERMISSION_DEFINITIONS.map(({ action, label, description }) => {
                const granted = actions.includes(action)
                return (
                  <div key={action} className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground">{description}</p>
                    </div>
                    {granted ? (
                      <Badge variant="secondary" className="shrink-0 text-green-600">
                        Granted
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="shrink-0 text-muted-foreground">
                        No Access
                      </Badge>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>Customize the visual theme of the application</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Theme</label>
            <Select value={theme} onValueChange={(value) => setTheme(value as any)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a theme" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="midnight">Midnight</SelectItem>
                <SelectItem value="matrix">Matrix</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Choose your preferred theme for the application
            </p>
          </div>
        </CardContent>
      </Card>

      {/* UI Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>UI Configuration</CardTitle>
          <CardDescription>
            Show or hide navigation items in the sidebar. Hidden views are still accessible via
            direct URL.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {navigation.map((item) => {
            const isSettings = item.href === '/settings'
            const visible = !hiddenNavItems.includes(item.href)
            return (
              <div key={item.href} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{item.name}</span>
                  {isSettings && (
                    <span className="text-xs text-muted-foreground">(always visible)</span>
                  )}
                </div>
                <Switch
                  checked={isSettings ? true : visible}
                  onCheckedChange={(checked) => setNavItemVisible(item.href, checked)}
                  disabled={isSettings}
                />
              </div>
            )
          })}
        </CardContent>
      </Card>

      {/* About */}
      <Card>
        <CardHeader>
          <CardTitle>About</CardTitle>
          <CardDescription>Application information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Application:</span>
            <span className="font-medium">XENON Flex Demo UI</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Version:</span>
            <span className="font-medium">0.1.0</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Developer:</span>
            <span className="font-medium">gridX GmbH</span>
          </div>
        </CardContent>
      </Card>

      {/* Production Warning Dialog */}
      <AlertDialog open={showProdWarning} onOpenChange={setShowProdWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Switch to Production?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to switch to the production environment. Any changes you make will
              affect real systems and data.
              <br />
              <br />
              <strong>Production URL:</strong> https://api.gridx.de
              <br />
              <br />
              You will be logged out and need to log in again with production credentials.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmEnvironmentSwitch('production')}>
              Yes, Switch to Production
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Staging Warning Dialog */}
      <AlertDialog open={showStagingWarning} onOpenChange={setShowStagingWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-blue-500" />
              Switch to Staging?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to switch to the staging environment.
              <br />
              <br />
              <strong>Staging URL:</strong> https://api.staging.gridx.de
              <br />
              <br />
              You will be logged out and need to log in again with staging credentials.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmEnvironmentSwitch('staging')}>
              Yes, Switch to Staging
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
